import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { observer } from "mobx-react";

// 22px, deliberately off the 4px scale for now. `h-6` (24px) is where this
// belongs — it is the scale every control height uses, and it makes a badge
// exactly a `size="sm"` Button. But `WorkflowStatusPill` sets no height and
// lands at 22px, and the two sit in adjacent columns of the work-items table
// (Status is a pill, Priority is a badge), so moving one alone puts a 2px step
// between them. Both move together, in a change that can be reviewed as one
// decision — NG-410.
const badgeVariants = cva(
  "group/badge inline-flex h-[22px] w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-[11px] font-medium whitespace-nowrap tabular-nums transition-[color,background-color,border-color,box-shadow] duration-(--motion-duration-fast) focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-2.5!",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary: "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        destructive:
          "bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20",
        outline:
          "border-border bg-background text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
        "status-neutral": "bg-muted text-muted-foreground",
        "status-primary":
          "bg-primary/10 text-primary focus-visible:ring-primary/20 dark:bg-primary/20 dark:focus-visible:ring-primary/40",
        "status-success":
          "bg-success/10 text-success-text focus-visible:ring-success/20 dark:bg-success/20 dark:focus-visible:ring-success/40",
        "status-warning":
          "bg-warning/10 text-warning-text focus-visible:ring-warning/20 dark:bg-warning/20 dark:focus-visible:ring-warning/40",
        "status-info":
          "bg-info/10 text-info-text focus-visible:ring-info/20 dark:bg-info/20 dark:focus-visible:ring-info/40",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Badge = observer(function Badge({
  className,
  variant = "default",
  dot = false,
  render,
  children,
  ...props
}: useRender.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    /** Render a leading status dot in the current text color (design spec). */
    dot?: boolean;
  }) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
        children: (
          <>
            {dot ? (
              <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
            ) : null}
            {children}
          </>
        ),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  });
});

export { Badge };
